import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import "../css/LandingPage.css";

function normalizePostcodeInput(value: string): string {
  return value.replace(/\s+/g, " ").trim().toUpperCase();
}

function isLikelyFullUkPostcode(value: string): boolean {
  const normalized = normalizePostcodeInput(value);
  const fullPostcodeRegex =
    /^(GIR 0AA|[A-Z]{1,2}\d[A-Z\d]?\s\d[A-Z]{2})$/i;

  return fullPostcodeRegex.test(normalized);
}

const LandingPage: React.FC = () => {
  const navigate = useNavigate();

  const [ownerName, setOwnerName] = useState("");
  const [ownerEmail, setOwnerEmail] = useState("");
  const [ownerPostcode, setOwnerPostcode] = useState("");
  const [ownerNeed, setOwnerNeed] = useState("");
  const [ownerError, setOwnerError] = useState("");

  function goToWalkerSearch(postcode?: string) {
    const normalized = normalizePostcodeInput(postcode ?? ownerPostcode);

    if (!normalized) {
      setOwnerError("Please enter a postcode.");
      return;
    }

    if (!isLikelyFullUkPostcode(normalized)) {
      setOwnerError("Please enter a full UK postcode, for example IP1 1AA.");
      return;
    }

    setOwnerError("");
    navigate(`/walkers?postcode=${encodeURIComponent(normalized)}`);
  }

  function handleOwnerSubmit(e: React.FormEvent) {
    e.preventDefault();
    goToWalkerSearch(ownerPostcode);
  }

  return (
    <div className="wnm-page">
      <header className="wnm-header">
        <div className="wnm-shell wnm-header__inner">
          <Link to="/" className="wnm-brand" aria-label="WalkiesNearMe home">
            <img
              src="/walkies_logo_from_flyer_v3.png"
              alt="WalkiesNearMe"
              className="wnm-brand__logo"
            />
          </Link>

          <nav className="wnm-nav" aria-label="Primary navigation">
            <a href="#how-it-works">How it works</a>
            <a href="#for-walkers">For walkers</a>
            <a href="#for-owners">For owners</a>
            <Link to="/reply-access">Owner conversation access</Link>
            <Link to="/walker/access">Walker sign in</Link>
            <Link
              to="/join-walker"
              className="wnm-btn wnm-btn--small wnm-btn--primary"
            >
              Join as walker
            </Link>
          </nav>
        </div>
      </header>

      <main>
        <section className="wnm-hero">
          <div className="wnm-shell wnm-hero__grid">
            <div className="wnm-hero__content">
              <div className="wnm-badge">Local-first launch • early access now open</div>

              <h1 className="wnm-hero__title">
                NEED HELP
                <br />
                <span className="wnm-hero__titleAccent">WALKING</span> YOUR
                <br />
                <span className="wnm-hero__titleAccent">DOG?</span>
              </h1>

              <p className="wnm-hero__subtitle">
                Find trusted local dog walkers near you
              </p>

              <p className="wnm-hero__text">
                WalkiesNearMe helps dog owners find reliable local walkers and helps dog
                walkers register early to receive nearby enquiries as we launch area by
                area.
              </p>

              <div className="wnm-hero__actions">
                <a href="#register-owner" className="wnm-btn wnm-btn--primary">
                  I need a dog walker
                </a>
                <Link to="/join-walker" className="wnm-btn wnm-btn--secondary">
                  I’m a dog walker
                </Link>
                <Link to="/reply-access" className="wnm-btn wnm-btn--secondary">
                  Reopen my conversation
                </Link>
                <Link to="/walker/dashboard" className="wnm-btn wnm-btn--secondary">
                  Walker dashboard
                </Link>
              </div>

              <div className="wnm-ribbon" aria-label="Website address">
                <span>WalkiesNearMe.co.uk</span>
              </div>

              <div className="wnm-statgrid">
                <div className="wnm-statcard">
                  <strong>Trusted local walkers</strong>
                  <span>Built around local discovery and quality local leads.</span>
                </div>

                <div className="wnm-statcard">
                  <strong>Ideal while you’re at work</strong>
                  <span>Designed for busy dog owners who need dependable help.</span>
                </div>

                <div className="wnm-statcard">
                  <strong>Flexible &amp; reliable</strong>
                  <span>Start simple now, then grow into full marketplace features.</span>
                </div>
              </div>
            </div>

            <div className="wnm-hero__visual">
              <div className="wnm-dogShowcase">
                <div className="wnm-dogShowcase__photoPanel">
                  <img
                    src="/walkies_dog_from_flyer_v2.png"
                    alt="Happy dog out on a walk"
                    className="wnm-dogShowcase__dog"
                  />
                </div>

                <div className="wnm-dogShowcase__card">
                  <div className="wnm-dogShowcase__headline">
                    Find trusted local dog walkers
                  </div>
                  <div className="wnm-dogShowcase__caption">
                    Local discovery for dog owners. Early lead flow for walkers.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="wnm-creamBand">
          <div className="wnm-shell">
            <div className="wnm-creamBand__inner">
              <div className="wnm-creamBand__copy">
                <div className="wnm-logoRow">
                  <img
                    src="/walkies_logo_from_flyer_v3.png"
                    alt="WalkiesNearMe"
                    className="wnm-logoRow__logo"
                  />
                </div>

                <div className="wnm-bannerTitle">Find trusted local dog walkers</div>

                <ul className="wnm-checklist">
                  <li>Trusted local walkers</li>
                  <li>Ideal while you’re at work</li>
                  <li>Flexible &amp; reliable</li>
                </ul>
              </div>

              <div className="wnm-qrPanel" aria-label="QR code placeholder">
                <div className="wnm-qrPanel__code">QR</div>
                <div className="wnm-qrPanel__label">Scan to join early access</div>
                <div className="wnm-qrPanel__subtext">walkiesnearme.co.uk</div>
              </div>
            </div>
          </div>
        </section>

        <section className="wnm-formsSection">
          <div className="wnm-shell">
            <div className="wnm-formsIntro">
              <span className="wnm-kicker">Join early</span>
              <h2>Whether you need a walker or are a walker, register now</h2>
              <p>
                We are validating local demand first, then expanding into a fuller
                marketplace experience.
              </p>
            </div>

            <div className="wnm-formsGrid">
              <section id="register-owner" className="wnm-panel wnm-panel--light">
                <div className="wnm-panel__icon" aria-hidden="true">
                  🐶
                </div>
                <h2>Need a local dog walker?</h2>
                <p>
                  Register your interest and tell us where you are so we can build local
                  walker coverage where it is needed most.
                </p>

                <form className="wnm-form" onSubmit={handleOwnerSubmit}>
                  <input
                    type="text"
                    placeholder="Your name"
                    value={ownerName}
                    onChange={(e) => setOwnerName(e.target.value)}
                  />
                  <input
                    type="email"
                    placeholder="Email address"
                    value={ownerEmail}
                    onChange={(e) => setOwnerEmail(e.target.value)}
                  />
                  <input
                    type="text"
                    placeholder="Full postcode, e.g. IP1 1AA"
                    value={ownerPostcode}
                    onChange={(e) => setOwnerPostcode(e.target.value)}
                  />
                  <textarea
                    rows={4}
                    placeholder="What sort of dog walking help are you looking for?"
                    value={ownerNeed}
                    onChange={(e) => setOwnerNeed(e.target.value)}
                  />

                  {ownerError && <div className="wnm-formError">{ownerError}</div>}

                  <button
                    type="button"
                    className="wnm-btn wnm-btn--secondary wnm-btn--full"
                    onClick={() => goToWalkerSearch(ownerPostcode)}
                  >
                    Search dog walkers now
                  </button>

                  <button
                    type="submit"
                    className="wnm-btn wnm-btn--primary wnm-btn--full"
                  >
                    Register your interest
                  </button>
                </form>

                <div style={{ marginTop: 16 }}>
                  <Link to="/reply-access" className="wnm-btn wnm-btn--secondary wnm-btn--full">
                    Already contacted a walker? Reopen your conversation
                  </Link>
                </div>
              </section>

              <section id="join-walker" className="wnm-panel wnm-panel--dark">
                <div className="wnm-panel__icon" aria-hidden="true">
                  🦮
                </div>
                <h2>Join as a dog walker</h2>
                <p>
                  Join early, tell us your town and service area, and be one of the first
                  walkers featured when we open up local enquiries.
                </p>

                <div className="wnm-form">
                  <Link
                    to="/join-walker"
                    className="wnm-btn wnm-btn--secondary wnm-btn--full"
                  >
                    Create walker profile
                  </Link>

                  <Link
                    to="/walker/access"
                    className="wnm-btn wnm-btn--primary wnm-btn--full"
                  >
                    Sign in to walker dashboard
                  </Link>
                </div>
              </section>
            </div>
          </div>
        </section>

        <section id="how-it-works" className="wnm-section">
          <div className="wnm-shell">
            <div className="wnm-section__heading">
              <span className="wnm-kicker">How it works</span>
              <h2>Simple marketplace flow, built local-first</h2>
              <p>
                We are following a practical approach: validate demand first, keep the
                flow simple, and layer on richer features once the core marketplace is
                working.
              </p>
            </div>

            <div className="wnm-steps">
              <article className="wnm-step">
                <div className="wnm-step__number">1</div>
                <h3>Dog owners register need</h3>
                <p>
                  Owners tell us where they are and what kind of walking help they need.
                </p>
              </article>

              <article className="wnm-step">
                <div className="wnm-step__number">2</div>
                <h3>Walkers join locally</h3>
                <p>
                  Dog walkers create their public profiles and describe the services they
                  offer.
                </p>
              </article>

              <article className="wnm-step">
                <div className="wnm-step__number">3</div>
                <h3>We connect both sides</h3>
                <p>
                  As local coverage grows, enquiries and matches can flow through the
                  marketplace.
                </p>
              </article>
            </div>
          </div>
        </section>

        <section id="for-walkers" className="wnm-section wnm-section--alt">
          <div className="wnm-shell">
            <div className="wnm-two-col">
              <div>
                <span className="wnm-kicker">For dog walkers</span>
                <h2>Grow your walking business in the areas that matter</h2>
                <p>
                  WalkiesNearMe is being designed to help independent local walkers get
                  discovered by nearby owners already looking for help.
                </p>

                <ul className="wnm-list">
                  <li>Create your local public profile now</li>
                  <li>Build trust with services and trust signals</li>
                  <li>Receive nearby leads instead of broad untargeted traffic</li>
                  <li>Start simple before supporter or subscription features arrive</li>
                </ul>
              </div>

              <div className="wnm-feature-card">
                <h3>Why join early?</h3>
                <p>
                  Early walker profiles help seed local coverage and make the marketplace
                  useful sooner.
                </p>
                <Link to="/join-walker" className="wnm-btn wnm-btn--secondary">
                  Create your profile
                </Link>
              </div>
            </div>
          </div>
        </section>

        <section id="for-owners" className="wnm-section">
          <div className="wnm-shell">
            <div className="wnm-two-col">
              <div className="wnm-feature-card wnm-feature-card--cream">
                <h3>Looking for help with your dog?</h3>
                <p>
                  Register your area now so we can prioritise the places where real local
                  demand already exists.
                </p>
                <a href="#register-owner" className="wnm-btn wnm-btn--primary">
                  Register interest
                </a>
                <Link to="/reply-access" className="wnm-btn wnm-btn--secondary">
                  Reopen my conversation
                </Link>
              </div>

              <div>
                <span className="wnm-kicker">For dog owners</span>
                <h2>Find trusted local dog walkers near you</h2>
                <p>
                  The first version of WalkiesNearMe is about making discovery and enquiry
                  easy, not overcomplicating things with booking and payment before the
                  core local marketplace is proven.
                </p>

                <ul className="wnm-list">
                  <li>Register your postcode and need</li>
                  <li>Help shape launch coverage in your town</li>
                  <li>Connect through a simple enquiry-led model</li>
                  <li>Reopen past conversations securely by email if needed</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section className="wnm-cta">
          <div className="wnm-shell">
            <div className="wnm-cta__box">
              <div>
                <span className="wnm-kicker">Get involved early</span>
                <h2>Whether you walk dogs or need a walker, join now</h2>
                <p>Help us launch WalkiesNearMe in the right local areas first.</p>
              </div>

              <div className="wnm-cta__actions">
                <a href="#register-owner" className="wnm-btn wnm-btn--primary">
                  Need a walker
                </a>
                <Link to="/join-walker" className="wnm-btn wnm-btn--secondary">
                  Join as walker
                </Link>
                <Link to="/reply-access" className="wnm-btn wnm-btn--secondary">
                  Reopen conversation
                </Link>
                <Link to="/walker/access" className="wnm-btn wnm-btn--secondary">
                  Walker sign in
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      <footer className="wnm-footer">
        <div className="wnm-shell wnm-footer__inner">
          <div>
            <strong>WalkiesNearMe</strong>
            <p>walkiesnearme.co.uk</p>
          </div>

          <div className="wnm-footer__links">
            <a href="#how-it-works">How it works</a>
            <a href="#for-walkers">For walkers</a>
            <a href="#for-owners">For owners</a>
            <Link to="/reply-access">Owner conversation access</Link>
            <Link to="/walker/access">Walker sign in</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;